/** biome-ignore-all lint/complexity/noStaticOnlyClass: it's ok */
import { isNetworkErrorPayload } from "@ext/errorHandlers/network/NetworkErrorPayload";
import t from "@ext/localization/locale/translate";
import { type Dispatch, type ReactElement, type SetStateAction, useState } from "react";
import DefaultError from "../logic/DefaultError";
import ErrorModal from "./components/ErrorModal";

let SetError: Dispatch<SetStateAction<DefaultError>>;

abstract class ErrorConfirmService {
	private static _isWork: boolean;
	private static _onClose: () => Promise<void>;
	private static _onOpen: () => Promise<void>;

	static start() {
		ErrorConfirmService._isWork = true;
	}

	static stop() {
		ErrorConfirmService._isWork = false;
	}

	static Provider({ children }: { children: ReactElement }): ReactElement {
		ErrorConfirmService.start();
		const [error, setError] = useState<DefaultError>(null);
		SetError = setError;

		return (
			<>
				<ErrorModal error={error} setError={setError} />
				{children}
			</>
		);
	}

	static set onModalOpen(onOpen: () => Promise<void>) {
		this._onOpen = async () => {
			if (!onOpen) return;
			await onOpen();
			ErrorConfirmService.onModalOpen = undefined;
		};
	}

	static get onModalOpen() {
		return this._onOpen;
	}

	static set onModalClose(onClose: () => Promise<void>) {
		this._onClose = async () => {
			if (!onClose) return;
			await onClose();
			ErrorConfirmService.onModalClose = undefined;
		};
	}

	static get onModalClose() {
		return this._onClose;
	}

	static notify(error: DefaultError) {
		if (!this._isWork) return;
		if (this._errorFilter(error)) return;
		if (!SetError) throw new Error("ErrorConfirmService error: no '_setError'");
		SetError(this._hasNothingToShow(error) ? this._withCommandFailedText(error) : error);
	}

	private static _hasNothingToShow(error: DefaultError): boolean {
		if (!error || error.message?.trim()) return false;
		const errorCode = error.props?.errorCode;
		return !errorCode || errorCode === "default";
	}

	private static _withCommandFailedText(error: DefaultError): DefaultError {
		const props = { ...error.props, html: true };
		return new DefaultError(
			t("app.error.command-failed.body"),
			error.cause,
			props,
			false,
			t("app.error.command-failed.title"),
		);
	}

	private static _errorFilter(error: DefaultError): boolean {
		return isNetworkErrorPayload(error);
	}
}

export default ErrorConfirmService;
